import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MembershipAPI } from "../../api/membership";
import {
  IconButton,
  MenuItem,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import VisibilityIcon from "../../assets/SVGs/Login/VisibilityIcon";
import { defaultEditFormState } from "../constant";
import WeeklyMeetEditModal from "../Modal/WeeklyMeetEditModal";
import EditReferralModal from "../Modal/EditReferralModal";
import EditClosedBusinessModal from "../Modal/EditClosedBusinessModal";
import EditMeetUpModal from "../Modal/EditMeetUpModal";
import EditVisitorModal from "../Modal/EditVisitorModal";

const PointsTab = ({ id }) => {
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(
    dayjs().subtract(1, "year").format("YYYY-MM-DD"),
  );
  const [openModal, setOpenModal] = useState(defaultEditFormState);

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(
        await MembershipAPI.getMemberPoints(
          id,
          timeframe,
          dayjs().format("YYYY-MM-DD"),
        ),
      );
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    isLoading && fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isLoading, timeframe]);

  const updatedData = data?.items?.map((point, index) => ({
    ...point,
    id: index + 1, // Assigns a unique ID starting from 1
  }));

  const columns = [
    {
      field: "points_received",
      headerName: "Points Received",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
    },
    {
      field: "activity_name",
      headerName: "Activity",
      flex: 1,
      minWidth: isMobileTable ? 200 : undefined,
    },
    {
      field: "received_date",
      headerName: "Received Date",
      flex: 1,
      minWidth: isMobileTable ? 200 : undefined,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton aria-label="view" size="small">
          <Link
            className="flex items-center justify-center"
            onClick={() =>
              setOpenModal({
                open: true,
                id: params?.row?.activity_id,
                type: params?.row?.activity_name,
                title: params?.row?.received_date,
              })
            }
          >
            <VisibilityIcon
              sx={{
                color: "#64748B",
              }}
            />
          </Link>
        </IconButton>
      ),
    },
  ];
  return (
    <>
      <div className="flex gap-16 align-center">
        <TextField
          value={data?.total_points_received || ""}
          className="login-textField"
          size="small"
          label="Total Points Amount"
          onChange={(e) => e.target.value}
          id="total-points-received"
          name="Total Points Amount"
          readOnly
          sx={{
            width: "178px",
            pointerEvents: "none",
            "& .MuiInputBase-input": {
              cursor: "default",
            },
          }}
        />
        <div style={{ fontSize: "14px", lineHeight: "20px", color: "#0F172A" }}>
          per
        </div>
        <TextField
          margin="normal"
          fullWidth
          id="status"
          label="Status*"
          name="status"
          value={timeframe}
          onChange={(e) => {
            setTimeframe(e.target.value);
            setIsLoading(true);
          }}
          className="login-textField "
          size="small"
          select
          sx={{
            width: "178px",
          }}
        >
          <MenuItem value={dayjs().subtract(1, "year").format("YYYY-MM-DD")}>
            1 year
          </MenuItem>
          <MenuItem value={dayjs().subtract(6, "month").format("YYYY-MM-DD")}>
            6 months
          </MenuItem>
          <MenuItem value={dayjs().subtract(3, "month").format("YYYY-MM-DD")}>
            3 months
          </MenuItem>
          <MenuItem value={dayjs().subtract(1, "month").format("YYYY-MM-DD")}>
            1 months
          </MenuItem>
        </TextField>
      </div>
      <div className="sub-header">Points Received</div>
      <Stack
        sx={{
          width: "100%",
          "& .MuiDataGrid-root": {
            borderRadius: "8px",
          },
        }}
        className="customDataGridFigma"
      >
        <DataGrid
          rows={updatedData || []}
          columns={columns}
          loading={isLoading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          checkboxSelection={false}
          rowSelection={false}
          rowHeight={48}
          columnHeaderHeight={40}
          getRowClassName={(params) => {
            const customClassName =
              params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
            return ` ${customClassName}`.trim();
          }}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
          localeText={{
            noRowsLabel: "No visitor found.",
          }}
        />
      </Stack>
      {openModal?.type === "Weekly Meet Attendance" && openModal?.open && (
        <WeeklyMeetEditModal
          open={openModal?.open}
          id={openModal?.id}
          handleClose={() => setOpenModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openModal?.title}
        />
      )}

      {openModal?.type === "Referrals Given" && openModal?.open && (
        <EditReferralModal
          open={openModal?.open}
          id={openModal?.id}
          handleClose={() => setOpenModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
        />
      )}

      {openModal?.type === "Closed Business Given" && openModal?.open && (
        <EditClosedBusinessModal
          open={openModal?.open}
          id={openModal?.id}
          handleClose={() => setOpenModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
        />
      )}

      {openModal?.type === "Meet-Ups" && openModal?.open && (
        <EditMeetUpModal
          open={openModal?.open}
          id={openModal?.id}
          handleClose={() => setOpenModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openModal?.title}
        />
      )}

      {openModal?.type === "Visitors Brought" && openModal?.open && (
        <EditVisitorModal
          open={openModal?.open}
          id={openModal?.id}
          handleClose={() => setOpenModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
        />
      )}
    </>
  );
};

export default PointsTab;
